import { DBSchema, IDBPDatabase, openDB } from 'idb';
import { CRMAccountSettings } from '@alucio/aws-beacon-amplify/src/models';

import { CRMAccount } from './CRMIndexedDBTypes';
import {
  buildCRMAccountSearchText,
  buildCRMAccountSearchRows,
  CRM_ACCOUNT_SEARCH_BACKFILL_MARKER_ID,
  CRM_ACCOUNT_SEARCH_BACKFILL_MARKER_VALUE,
  CRM_ACCOUNT_SEARCH_TOKEN_INDEX_LENGTH,
  CRM_ACCOUNT_SEARCH_TOKEN_PREFIX_LIMIT,
  CRMAccountSearchToken,
  tokenizeCRMAccountSearchText,
} from './CRMAccountSearchIndex';

export type AccountSearchBackfillStatus = 'in_progress' | typeof CRM_ACCOUNT_SEARCH_BACKFILL_MARKER_VALUE;

export type AccountSearchBackfillMarker = {
  status: AccountSearchBackfillStatus;
  lastAccountId?: string;
  processedCount?: number;
  accountCount?: number;
  configHash?: string;
};

type CRMAccountSearchMeta = {
  id: string;
  value: string;
};

enum CRMAccountSearchTableEnum {
  ACCOUNT_SEARCH = 'ACCOUNT_SEARCH',
  ACCOUNT_SEARCH_META = 'ACCOUNT_SEARCH_META',
}

interface CRMAccountSearchDBSchema extends DBSchema {
  ACCOUNT_SEARCH: {
    key: string;
    value: CRMAccountSearchToken;
    indexes: { token: string, accountId: string };
  };
  ACCOUNT_SEARCH_META: {
    key: string;
    value: CRMAccountSearchMeta;
    indexes: { id: string };
  };
}

export class CRMAccountSearchDB {
  readonly DB_NAME = 'CRMAccountSearchDb';
  readonly VERSION = 1;

  private openIDB = (() => {
    let openIDB: Promise<IDBPDatabase<CRMAccountSearchDBSchema>> | null = null;
    return () => {
      if (!openIDB) {
        openIDB = openDB<CRMAccountSearchDBSchema>(this.DB_NAME, this.VERSION, {
          upgrade(db) {
            if (!db.objectStoreNames.contains(CRMAccountSearchTableEnum.ACCOUNT_SEARCH)) {
              const accountSearchStore = db.createObjectStore(
                CRMAccountSearchTableEnum.ACCOUNT_SEARCH,
                { keyPath: 'id' },
              );
              accountSearchStore.createIndex('token', 'token');
              accountSearchStore.createIndex('accountId', 'accountId');
            }
            if (!db.objectStoreNames.contains(CRMAccountSearchTableEnum.ACCOUNT_SEARCH_META)) {
              db.createObjectStore(CRMAccountSearchTableEnum.ACCOUNT_SEARCH_META, { keyPath: 'id' });
            }
          },
        });
      }
      return openIDB;
    };
  })();

  private getDB = async () => {
    const db = await this.openIDB();

    if (!db) {
      throw new Error('CRM account search DB not initialized');
    }

    return db;
  };

  getIndexedTokenCount = async (): Promise<number> => {
    const db = await this.getDB();
    const tx = db.transaction(CRMAccountSearchTableEnum.ACCOUNT_SEARCH, 'readonly');
    const count = await tx.store.count();
    await tx.done;
    return count;
  };

  getBackfillMarker = async (): Promise<AccountSearchBackfillMarker | null> => {
    const db = await this.getDB();
    const tx = db.transaction(CRMAccountSearchTableEnum.ACCOUNT_SEARCH_META, 'readonly');
    const marker = await tx.store.get(CRM_ACCOUNT_SEARCH_BACKFILL_MARKER_ID);
    await tx.done;

    if (!marker?.value) {
      return null;
    }

    if (marker.value === CRM_ACCOUNT_SEARCH_BACKFILL_MARKER_VALUE) {
      return { status: CRM_ACCOUNT_SEARCH_BACKFILL_MARKER_VALUE };
    }

    try {
      const parsed = JSON.parse(marker.value) as AccountSearchBackfillMarker;
      if (parsed.status === 'in_progress' || parsed.status === CRM_ACCOUNT_SEARCH_BACKFILL_MARKER_VALUE) {
        return parsed;
      }

      return null;
    } catch {
      return null;
    }
  };

  setBackfillMarker = async (marker: AccountSearchBackfillMarker): Promise<void> => {
    const db = await this.getDB();
    const tx = db.transaction(CRMAccountSearchTableEnum.ACCOUNT_SEARCH_META, 'readwrite');
    await tx.store.put({
      id: CRM_ACCOUNT_SEARCH_BACKFILL_MARKER_ID,
      value: JSON.stringify(marker),
    });
    await tx.done;
  };

  resetBackfillMarker = async (): Promise<void> => {
    const db = await this.getDB();
    const tx = db.transaction(CRMAccountSearchTableEnum.ACCOUNT_SEARCH_META, 'readwrite');
    await tx.store.delete(CRM_ACCOUNT_SEARCH_BACKFILL_MARKER_ID);
    await tx.done;
  };

  getBackfillConfigHash = (config: CRMAccountSettings | null): string => {
    return JSON.stringify(config ?? null);
  };

  clearIndex = async (): Promise<void> => {
    const db = await this.getDB();
    await db.clear(CRMAccountSearchTableEnum.ACCOUNT_SEARCH);
    await this.resetBackfillMarker();
  };

  putAccountSearchRows = async (
    accounts: CRMAccount[],
    config: CRMAccountSettings | null,
  ): Promise<number> => {
    const db = await this.getDB();
    const tx = db.transaction(CRMAccountSearchTableEnum.ACCOUNT_SEARCH, 'readwrite');
    const store = tx.store;
    let indexedTokenCount = 0;

    await Promise.all(accounts.map(async (account) => {
      const searchRows = buildCRMAccountSearchRows(account, config);
      indexedTokenCount += searchRows.length;
      await Promise.all(searchRows.map(searchToken => store.put(searchToken)));
    }));

    await tx.done;

    return indexedTokenCount;
  };

  private getSearchTokens = (searchText: string | string[]): string[] => {
    const values = Array.isArray(searchText) ? searchText : [searchText];
    return values.flatMap(tokenizeCRMAccountSearchText);
  };

  doesAccountMatchSearch = (
    account: CRMAccount,
    config: CRMAccountSettings | null,
    searchText: string | string[],
  ): boolean => {
    const searchTokens = this.getSearchTokens(searchText);
    const accountTokens = tokenizeCRMAccountSearchText(buildCRMAccountSearchText(account, config));

    return searchTokens.every(searchToken => (
      accountTokens.some(accountToken => accountToken.startsWith(searchToken))
    ));
  };

  searchAccountIdsByTokenPrefixes = async (searchText: string | string[]): Promise<string[]> => {
    const normalizedTokens = this.getSearchTokens(searchText);

    if (!normalizedTokens.length) {
      return [];
    }

    const db = await this.getDB();
    const tx = db.transaction(CRMAccountSearchTableEnum.ACCOUNT_SEARCH, 'readonly');
    const tokenIndex = tx.store.index('token');
    const accountScore: Record<string, number> = {};

    for (const token of normalizedTokens) {
      const lookupToken = token.slice(0, CRM_ACCOUNT_SEARCH_TOKEN_INDEX_LENGTH);
      const range = IDBKeyRange.bound(lookupToken, `${lookupToken}\uffff`);
      let cursor = await tokenIndex.openCursor(range);
      let tokenMatches = 0;

      while (cursor && tokenMatches < CRM_ACCOUNT_SEARCH_TOKEN_PREFIX_LIMIT) {
        const value = cursor.value as CRMAccountSearchToken;
        accountScore[value.accountId] = (accountScore[value.accountId] || 0) + 1;
        tokenMatches += 1;
        cursor = await cursor.continue();
      }
    }

    await tx.done;

    return Object.entries(accountScore)
      .sort(([, scoreA], [, scoreB]) => scoreB - scoreA)
      .map(([accountId]) => accountId);
  };
}
